/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { keys, useMouseMove } from '../../services';
import { isNil } from '../../services/predicate';
import { useEuiI18n } from '../i18n';
import { getEventPosition } from './utils';
export var EuiSaturation = forwardRef(({
  className,
  color = [1, 0, 0],
  'data-test-subj': dataTestSubj = 'euiSaturation',
  hex,
  id,
  onChange,
  ...rest
}, ref) => {
  const [roleDescString, instructionsString] = useEuiI18n(['euiSaturation.ariaLabel', 'euiSaturation.screenReaderInstructions'], ['HSV color mode saturation and value 2-axis slider', "Arrow keys to navigate the square color gradient. Coordinates will be used to calculate HSV color mode 'saturation' and 'value' numbers, in the range of 0 to 1. Left and right to change the saturation. Up and down change the value."]);
  const [indicator, setIndicator] = useState({
    left: 0,
    top: 0
  });
  const [lastColor, setLastColor] = useState([]);
  const boxRef = useRef(null);
  useEffect(() => {
    // Mimics `componentDidMount` and `componentDidUpdate`
    const [, s, v] = color;

    if (!isNil(boxRef.current) && lastColor.join() !== color.join()) {
      const {
        height,
        width
      } = boxRef.current.getBoundingClientRect();
      setIndicator({
        left: s * width,
        top: (1 - v) * height
      });
    }
  }, [color, lastColor]);

  const calculateColor = ({
    top,
    height,
    left,
    width
  }) => {
    const [h] = color;
    const s = left / width;
    const v = 1 - top / height;
    return [h, s, v];
  };

  const handleUpdate = box => {
    const {
      left,
      top
    } = box;
    setIndicator({
      left,
      top
    });
    const newColor = calculateColor(box);
    setLastColor(newColor);
    onChange(newColor);
  };

  const handleChange = location => {
    if (isNil(boxRef?.current)) return;
    const box = getEventPosition(location, boxRef.current);
    handleUpdate(box);
  };

  const [handleMouseDown, handleInteraction] = useMouseMove(handleChange, boxRef.current);

  const handleKeyDown = event => {
    if (isNil(boxRef?.current)) return;
    const {
      height,
      width
    } = boxRef.current.getBoundingClientRect();
    const {
      left,
      top
    } = indicator;
    const heightScale = height / 100;
    const widthScale = width / 100;
    let newLeft = left;
    let newTop = top;

    switch (event.key) {
      case keys.ARROW_DOWN:
        event.preventDefault();
        newTop = top < height ? top + heightScale : height;
        break;

      case keys.ARROW_LEFT:
        event.preventDefault();
        newLeft = left > 0 ? left - widthScale : 0;
        break;

      case keys.ARROW_UP:
        event.preventDefault();
        newTop = top > 0 ? top - heightScale : 0;
        break;

      case keys.ARROW_RIGHT:
        event.preventDefault();
        newLeft = left < width ? left + widthScale : width;
        break;

      default:
        return;
    }

    const newPosition = {
      left: newLeft,
      top: newTop
    };
    setIndicator(newPosition);
    const newColor = calculateColor({
      width,
      height,
      ...newPosition
    });
    onChange(newColor);
  };

  const classes = classNames('euiSaturation', className);
  const instructionsId = `${id}-instructions`;
  return <div onMouseDown={handleMouseDown} onTouchStart={handleInteraction} onTouchMove={handleInteraction} onKeyDown={handleKeyDown} ref={ref} className={classes} data-test-subj={dataTestSubj} style={{
    background: `hsl(${color[0]}, 100%, 50%)`
  }} tabIndex={-1} {...rest}>
        <div className="euiSaturation__lightness" ref={boxRef}>
          <div className="euiSaturation__saturation" />
        </div>
        <button id={`${id}-saturationIndicator`} className="euiSaturation__indicator" style={{ ...indicator
    }} aria-roledescription={roleDescString} aria-label={hex} aria-describedby={instructionsId} />
        <span hidden aria-live="assertive">
          {hex}
        </span>
        <span hidden id={instructionsId}>
          {instructionsString}
        </span>
      </div>;
});
EuiSaturation.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  color: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  hex: PropTypes.string
};
EuiSaturation.displayName = 'EuiSaturation';

try {
  EuiSaturation.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/color_picker/saturation.tsx',
    description: '',
    displayName: 'EuiSaturation',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      color: {
        defaultValue: {
          value: '[1, 0, 0]'
        },
        description: '',
        name: 'color',
        parent: {
          fileName: 'app/src/components/color_picker/saturation.tsx',
          name: 'HTMLDivElementOverrides'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/saturation.tsx',
          name: 'HTMLDivElementOverrides'
        }],
        required: false,
        type: {
          name: '[number, number, number]'
        }
      },
      onChange: {
        defaultValue: null,
        description: '',
        name: 'onChange',
        parent: {
          fileName: 'app/src/components/color_picker/saturation.tsx',
          name: 'HTMLDivElementOverrides'
        },
        declarations: [{
          fileName: 'app/src/components/color_picker/saturation.tsx',
          name: 'HTMLDivElementOverrides'
        }],
        required: true,
        type: {
          name: '(color: [number, number, number]) => void'
        }
      },
      hex: {
        defaultValue: null,
        description: '',
        name: 'hex',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/color_picker/saturation.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      ref: {
        defaultValue: null,
        description: '',
        name: 'ref',
        parent: {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'RefAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'RefAttributes'
        }],
        required: false,
        type: {
          name: 'Ref<HTMLDivElement>'
        }
      }
    },
    extendedInterfaces: ['HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'CommonProps', 'HTMLDivElementOverrides', 'Attributes']
  };
} catch (e) {}